import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Check, Upload } from 'lucide-react';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import SourceSelector, { SOURCE_MODE_CSV } from './components/SourceSelector';
import FileUpload from './components/FileUpload';
import PreviewData from './components/PreviewData';
import ColumnMapper from './components/ColumnMapper';
import ImportProgress from './components/ImportProgress';
import { getAvailableColumns, executeImport } from '@/services/importService';
import { toastMessage } from '@/utils/toast-message';
import type {
  ImportStep,
  ColumnMapping,
  CSVPreviewData,
  ImportStats,
  ImportError,
  CRMField
} from './types';

const ImportPage: React.FC = () => {
  // State Management
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedSourceName, setSelectedSourceName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<CSVPreviewData | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    imported: 0,
    duplicates: 0,
    errors: 0,
    progress: 0
  });
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportComplete, setIsImportComplete] = useState(false);
  const [crmFields, setCrmFields] = useState<CRMField[]>([]);

  // Fetch CRM columns from API on mount
  useEffect(() => {
    const fetchCrmColumns = async () => {
      try {
        const response = await getAvailableColumns();

        if (response.success && response.data) {
          // Transform API response to CRMField format
          const fields: CRMField[] = response.data.map((col) => ({
            field: col.col_name,
            label: col.col_label,
            uuid: col.uuid,
            type: col.col_type === 'phone' ? 'phone' :
                  col.col_type === 'email' ? 'email' :
                  col.col_type === 'date' ? 'date' :
                  col.col_type === 'number' ? 'number' :
                  col.col_type === 'textarea' ? 'textarea' :
                  col.col_type === 'select' ? 'select' : 'text',
            required: col.col_name === 'phone', // Only phone is mandatory
            options: col.options?.map(opt => opt.opt_name)
          }));

          setCrmFields(fields);
        } else {
          toastMessage('Failed to load CRM columns', 'error');
        }
      } catch (error) {
        console.error('Error fetching CRM columns:', error);
        toastMessage('Failed to load CRM columns', 'error');
      }
    };

    fetchCrmColumns();
  }, []);

  // Steps Configuration
  const steps: ImportStep[] = [
    {
      id: 0,
      title: 'Select Source',
      description: 'Choose where your leads are from',
      completed: selectedSource !== ''
    },
    {
      id: 1,
      title: 'Upload File',
      description: 'Upload your CSV file',
      completed: uploadedFile !== null && previewData !== null
    },
    {
      id: 2,
      title: 'Preview Data',
      description: 'Review your data',
      completed: previewData !== null
    },
    {
      id: 3,
      title: 'Map Columns',
      description: 'Match CSV columns to CRM fields',
      completed: columnMappings.some((m) => m.crmField !== '')
    },
    {
      id: 4,
      title: 'Import',
      description: 'Import your leads',
      completed: isImportComplete
    }
  ];

  // Handlers
  const handleFileSelect = (file: File) => {
    setUploadedFile(file);
  };

  const handleFileParse = (data: { columns: string[]; rows: any[]; totalRows: number; allData: any[] }) => {
    setPreviewData({
      columns: data.columns,
      rows: data.rows,
      totalRows: data.totalRows,
      allData: data.allData
    });

    // Initialize column mappings
    const initialMappings: ColumnMapping[] = data.columns.map((col) => ({
      csvColumn: col,
      crmField: '',
      crmFieldUuid: undefined
    }));
    setColumnMappings(initialMappings);
  };

  const handleStartImport = async () => {
    if (!previewData || !previewData.allData) return;

    setIsImporting(true);
    setIsImportComplete(false);

    try {
      // Build mapping in the format expected by backend: { [csvColumn]: { col_id: uuid } }
      const mapping: Record<string, { col_id: string; col_name?: string }> = {};

      columnMappings.forEach((m) => {
        if (m.crmField && m.crmFieldUuid) {
          mapping[m.csvColumn] = {
            col_id: m.crmFieldUuid,
            col_name: m.crmField
          };
        }
      });

      // Find source and status fields
      const sourceField = crmFields.find(f => f.field === 'source');
      const statusField = crmFields.find(f => f.field === 'status');

      // Check if source/status are already mapped from CSV
      const hasSourceInMapping = columnMappings.some(m => m.crmField === 'source');
      const hasStatusInMapping = columnMappings.some(m => m.crmField === 'status');

      // Determine source injection strategy based on Step 1 selection
      const isMapFromCSV = selectedSource === SOURCE_MODE_CSV;
      const isPresetSource = selectedSource && selectedSource !== SOURCE_MODE_CSV;

      // Get the source name to inject (only for preset sources)
      const sourceNameToInject = isPresetSource ? selectedSourceName : '';

      // For "Map from CSV" mode, source must be mapped from CSV columns
      // For preset source, inject the source if not mapped from CSV
      if (!isMapFromCSV && sourceNameToInject && sourceField) {
        // If source is not mapped from CSV, always inject
        if (!hasSourceInMapping) {
          mapping['__source__'] = {
            col_id: sourceField.uuid,
            col_name: 'source'
          };
          console.log('Injecting source from Step 1:', sourceNameToInject);
        }
        // If source IS mapped from CSV, the backend will use CSV values when available
        // and fall back to injected source when CSV column is empty
      }

      // Inject status if not mapped
      let defaultStatus = null;
      if (!hasStatusInMapping && statusField && statusField.options) {
        const newLeadOption = statusField.options.find(opt =>
          opt.toLowerCase().includes('new lead') || opt.toLowerCase() === 'new'
        );
        defaultStatus = newLeadOption || statusField.options[0];

        mapping['__status__'] = {
          col_id: statusField.uuid,
          col_name: 'status'
        };
        console.log('Auto-injecting status:', defaultStatus);
      }

      // Prepare data with injected source and status values where needed
      const enrichedData = previewData.allData.map(row => {
        const newRow = { ...row };

        // Inject source if Step 1 provided a source (and not "Map from CSV" mode)
        if (!isMapFromCSV && sourceNameToInject) {
          newRow['__source__'] = sourceNameToInject;
        }

        // Inject status if not mapped
        if (!hasStatusInMapping && defaultStatus) {
          newRow['__status__'] = defaultStatus;
        }

        return newRow;
      });

      // Debug log the final payload
      console.log('Import payload:', {
        mapping,
        firstRow: enrichedData[0],
        totalRows: enrichedData.length
      });

      // Call executeImport API with enriched data
      const response = await executeImport(enrichedData, mapping);

      if (response.success && response.data) {
        const { success: imported, failed: errorCount, skipped } = response.data;

        // Calculate duplicates from skipped array
        const duplicates = Array.isArray(skipped) ? skipped.length : 0;

        // Transform backend errors to ImportError format
        const errors: ImportError[] = [];
        if (Array.isArray(skipped)) {
          skipped.forEach((item) => {
            if (typeof item === 'object' && 'row' in item) {
              errors.push({
                row: item.row,
                field: item.phone || '',
                error: item.reason || 'Skipped'
              });
            }
          });
        }

        // Set final stats
        setImportStats({
          total: previewData.totalRows,
          imported,
          duplicates,
          errors: errorCount,
          progress: 100
        });

        setImportErrors(errors);
        setIsImportComplete(true); // Mark as complete only on success
        toastMessage(`Import completed: ${imported} leads imported, ${duplicates} duplicates skipped`, 'success');
      } else {
        throw new Error('Import failed');
      }
    } catch (error: any) {
      console.error('Import error:', error);
      const errorMessage = error.message || 'Import failed. Please try again.';
      toastMessage(errorMessage, 'error');

      // Reset progress on error
      setImportStats({
        total: previewData.totalRows,
        imported: 0,
        duplicates: 0,
        errors: 1,
        progress: 0
      });

      setImportErrors([{
        row: 0,
        error: errorMessage
      }]);

      // Don't mark as complete on error - allow user to try again
      setIsImportComplete(false);
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadErrors = () => {
    const csvContent =
      'Row,Field,Error\n' +
      importErrors.map((e) => `${e.row},${e.field || ''},${e.error}`).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import-errors.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Reset import complete state when going back from final step
      if (currentStep === 4) {
        setIsImportComplete(false);
      }
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedSource !== '';
      case 1:
        return uploadedFile !== null && previewData !== null;
      case 2:
        return previewData !== null;
      case 3:
        // Check if required fields are mapped
        const requiredFields = crmFields.filter((f) => f.required);
        const hasRequiredMappings = requiredFields.every((field) =>
          columnMappings.some((m) => m.crmField === field.field)
        );

        // If "Map from CSV" mode, source must be mapped
        if (selectedSource === SOURCE_MODE_CSV) {
          const hasSourceMapping = columnMappings.some((m) => m.crmField === 'source');
          return hasRequiredMappings && hasSourceMapping;
        }

        return hasRequiredMappings;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <>
      <ModernSettingHeader
        title="Import Leads"
        description="Import leads from CSV files in a few simple steps. Upload your CSV, map columns, and import up to 10,000 leads at once."
        icon={<Upload className="h-5 w-5" />}
      />

      <div className="p-6">
        <div className="max-w-6xl mx-auto">

        {/* Stepper Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all
                      ${
                        currentStep === index
                          ? 'bg-brandColor text-white ring-4 ring-cyan-200 dark:ring-cyan-800'
                          : step.completed
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }
                    `}
                  >
                    {step.completed ? <Check className="h-6 w-6" /> : index + 1}
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={`text-sm font-medium ${
                        currentStep === index
                          ? 'text-brandColor dark:text-cyan-400'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 hidden sm:block">
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 transition-colors ${
                      step.completed
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`
              flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors
              ${
                currentStep === 0
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
            `}
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>

          <div className="flex space-x-3">
            {currentStep === 4 && !isImporting && !isImportComplete && (
              <button
                type="button"
                onClick={handleStartImport}
                disabled={!canProceed()}
                className={`
                  flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors
                  ${
                    canProceed()
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                <span>Start Import</span>
              </button>
            )}

            {currentStep < steps.length - 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className={`
                  flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors
                  ${
                    canProceed()
                      ? 'bg-brandColor hover:bg-brand-hover text-white'
                      : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                <span>Next</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-6">
          {/* Step Content */}
          {currentStep === 0 && (
            <SourceSelector
              selectedSource={selectedSource}
              onSourceSelect={(uuid, name) => {
                setSelectedSource(uuid);
                setSelectedSourceName(name);
              }}
            />
          )}

          {currentStep === 1 && (
            <FileUpload
              onFileSelect={handleFileSelect}
              onFileParse={handleFileParse}
              isLoading={false}
            />
          )}

          {currentStep === 2 && previewData && <PreviewData previewData={previewData} />}

          {currentStep === 3 && previewData && (
            <ColumnMapper
              csvColumns={previewData.columns}
              crmFields={crmFields}
              mappings={columnMappings}
              onMappingsChange={setColumnMappings}
            />
          )}

          {currentStep === 4 && (
            <ImportProgress
              stats={importStats}
              isImporting={isImporting}
              isComplete={isImportComplete}
              errors={importErrors}
              onDownloadErrors={handleDownloadErrors}
            />
          )}
        </div>

        </div>
      </div>
    </>
  );
};

export default ImportPage;
